import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  Input,
  Button,
  Row,
  Col,
  Card,
  Tag,
  Space,
  Spin,
  Empty,
  Typography,
  Pagination,
} from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { createOrderActions } from "../../../../store/slices/create-order-slice/create.orders.slice";
import { cycleAvailableDishesService } from "../../../../services/menu-services/cycleAvailableDishes.service";
import { availableDishesService } from "../../../../services/menu-services/availableDishes.service";
import { DishType } from "../../../../constants/enums";

const { Text } = Typography;
const PAGE_SIZE = 10;

const DISH_TYPE_COLORS = {
  [DishType.BASE]: "blue",
  [DishType.PROTEIN]: "orange",
  [DishType.SAUCE]: "green",
  [DishType.REGULAR]: "default",
};

function getCycleDay(date, startDate, lengthDays) {
  const diff = dayjs(date).diff(dayjs(startDate), "day");
  return ((diff % lengthDays) + lengthDays) % lengthDays + 1;
}

function AddCustomDishModal({
  date,
  visible,
  onClose,
  typeFilter: initialTypeFilter,
  insertAtIndex,
  isMainDish,
}) {
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.language;
  const dispatch = useDispatch();

  const { customDishes } = useSelector((store) => store.createOrder);
  const { menuFormat, cycleConfig } = useSelector((store) => store.appSettings);

  const existingDishCounts = useMemo(() => {
    const counts = new Map();
    (customDishes[date] ?? []).forEach((d) => {
      counts.set(d.id, (counts.get(d.id) || 0) + 1);
    });
    return counts;
  }, [customDishes, date]);

  const [allDishes, setAllDishes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [typeFilter, setTypeFilter] = useState(initialTypeFilter || null);
  const [page, setPage] = useState(1);

  const fetchAvailableDishes = useCallback(async () => {
    try {
      setLoading(true);
      let items = [];
      if (menuFormat === "period_based") {
        const cycleDay =
          cycleConfig?.startDate
            ? getCycleDay(date, cycleConfig.startDate, cycleConfig.lengthDays || 7)
            : 1;
        items = await cycleAvailableDishesService.getByDay(cycleDay);
      } else {
        items = await availableDishesService.getAllSAvailableDishes(date);
      }
      setAllDishes((items || []).map((item) => item.dish).filter(Boolean));
    } catch (err) {
      console.error("Error fetching available dishes:", err);
      setAllDishes([]);
    } finally {
      setLoading(false);
    }
  }, [date, menuFormat, cycleConfig]);

  useEffect(() => {
    if (visible) {
      setPage(1);
      setSearchValue("");
      fetchAvailableDishes();
    }
  }, [visible, fetchAvailableDishes]);

  useEffect(() => {
    setPage(1);
  }, [searchValue, typeFilter]);

  const filteredDishes = useMemo(() => {
    let result = allDishes;
    if (typeFilter) {
      result = result.filter((d) => d.type === typeFilter);
    }
    if (searchValue.trim()) {
      const q = searchValue.trim().toLowerCase();
      result = result.filter((d) => {
        const name = d?.name?.[currentLanguage] || d?.name?.en || "";
        return name.toLowerCase().includes(q);
      });
    }
    return result;
  }, [allDishes, typeFilter, searchValue, currentLanguage]);

  const paginatedDishes = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredDishes.slice(start, start + PAGE_SIZE);
  }, [filteredDishes, page]);

  const getDishTotalCount = (dishId) => existingDishCounts.get(dishId) || 0;

  const handleAddDish = (dish) => {
    const dishData = {
      id: dish.id,
      name: dish.name,
      img: dish.img || "",
      type: dish.type || DishType.REGULAR,
      energy: dish.energy,
      proteins: dish.proteins,
      fats: dish.fats,
      carbs: dish.carbs,
      carbohydrates: dish.carbohydrates,
      weight: dish.weight,
      weightNetto: dish.weightNetto,
      price: dish.price,
      cost: dish.cost,
    };

    if (insertAtIndex !== undefined && insertAtIndex !== null) {
      dispatch(
        createOrderActions.insertCustomDishAt({
          date,
          dish: dishData,
          index: insertAtIndex,
        }),
      );
      onClose();
      return;
    }

    dispatch(createOrderActions.addCustomDish({ date, dish: dishData }));
  };

  const handleTypeFilter = (type) => {
    setTypeFilter((prev) => (prev === type ? null : type));
  };

  const getDishName = (dish) =>
    dish?.name?.[currentLanguage] || dish?.name?.en || "-";

  const dishTypes = isMainDish
    ? [DishType.BASE, DishType.REGULAR]
    : [DishType.PROTEIN, DishType.SAUCE];

  return (
    <Modal
      title={t("Add Dish")}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnHidden
    >
      <div style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder={t("Search dishes...")}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          size="large"
          allowClear
        />
      </div>

      <div
        style={{ marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap" }}
      >
        {dishTypes.map((value) => (
          <Button
            key={value}
            type={typeFilter === value ? "primary" : "default"}
            onClick={() => handleTypeFilter(value)}
            style={{
              borderColor: typeFilter === value ? undefined : "#d9d9d9",
            }}
          >
            {value}
          </Button>
        ))}
        {typeFilter && (
          <Button onClick={() => setTypeFilter(null)}>
            {t("Clear filter")}
          </Button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
        </div>
      ) : paginatedDishes.length === 0 ? (
        <Empty description={t("No dishes found")} />
      ) : (
        <>
          <Row gutter={[12, 12]}>
            {paginatedDishes.map((dish) => {
              const count = getDishTotalCount(dish.id);
              return (
                <Col key={dish.id} xs={24} sm={12} md={8}>
                  <Card
                    hoverable
                    size="small"
                    cover={
                      dish.img ? (
                        <img
                          alt={getDishName(dish)}
                          src={dish.img}
                          style={{ height: 120, objectFit: "cover" }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/images/photoForGoFit.jpg";
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            height: 120,
                            background: "#f5f5f5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Text type="secondary">{t("No image")}</Text>
                        </div>
                      )
                    }
                  >
                    <div style={{ marginBottom: 4 }}>
                      {dish.type && (
                        <Tag
                          color={DISH_TYPE_COLORS[dish.type]}
                          style={{ fontSize: 10, textTransform: "uppercase" }}
                        >
                          {dish.type}
                        </Tag>
                      )}
                    </div>
                    <Text
                      strong
                      ellipsis
                      style={{ display: "block", marginBottom: 4 }}
                    >
                      {getDishName(dish)}
                    </Text>
                    <Space size={4} wrap style={{ marginBottom: 8 }}>
                      <Tag style={{ fontSize: 11, margin: 0 }}>
                        {Math.round(Number(dish.energy) || 0)} kcal
                      </Tag>
                      <Tag style={{ fontSize: 11, margin: 0 }}>
                        {Math.round(Number(dish.weight) || 0)}g
                      </Tag>
                    </Space>
                    <div style={{ marginBottom: 8 }}>
                      <Space size={4}>
                        <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>
                          P:{Math.round(Number(dish.proteins) || 0)}
                        </Tag>
                        <Tag color="gold" style={{ fontSize: 11, margin: 0 }}>
                          F:{Math.round(Number(dish.fats) || 0)}
                        </Tag>
                        <Tag color="green" style={{ fontSize: 11, margin: 0 }}>
                          C:
                          {Math.round(
                            Number(dish.carbohydrates) ||
                              Number(dish.carbs) ||
                              0,
                          )}
                        </Tag>
                      </Space>
                    </div>
                    {dish.price != null && (
                      <div style={{ marginBottom: 8 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {t("Price")}: {Number(dish.price).toFixed(2)}
                        </Text>
                      </div>
                    )}
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      block
                      onClick={() => handleAddDish(dish)}
                    >
                      {count > 0
                        ? `${t("Add Dish")} (×${count})`
                        : t("Add Dish")}
                    </Button>
                  </Card>
                </Col>
              );
            })}
          </Row>
          {filteredDishes.length > PAGE_SIZE && (
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <Pagination
                current={page}
                total={filteredDishes.length}
                pageSize={PAGE_SIZE}
                onChange={(p) => setPage(p)}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

export default AddCustomDishModal;
